import { useMemo } from 'react';
import type { List, Space, Status, Tag, Task } from '@karya/core';
import { usePendingKeys, useTaskTagMap, useWorkspace } from '@/data/queries';

export interface ResolvedTask {
  list: List | undefined;
  space: Space | undefined;
  tags: Tag[];
  status: Status | undefined;
  statuses: Status[];
  pending: boolean;
}

/** Everything a TaskRow needs about a task's context, resolved from the local mirror. */
export function useTaskRowData() {
  const ws = useWorkspace();
  const tagMap = useTaskTagMap();
  const pending = usePendingKeys();
  // Results are cached per task object, so rows get identical props between renders and
  // memoised rows don't re-render while a long list scrolls.
  const resolve = useMemo(() => {
    const cache = new WeakMap<Task, ResolvedTask>();
    return (task: Task): ResolvedTask => {
      const hit = cache.get(task);
      if (hit) return hit;
      const list = ws.listById.get(task.list_id);
      const resolved: ResolvedTask = {
        list,
        space: list ? ws.spaceById.get(list.space_id) : undefined,
        tags: (tagMap.get(task.id) ?? [])
          .map((id) => ws.tagById.get(id))
          .filter((t): t is Tag => Boolean(t)),
        status: ws.statusById.get(task.status_id),
        statuses: ws.statusesForList(task.list_id),
        pending: pending.has(`tasks:${task.id}`),
      };
      cache.set(task, resolved);
      return resolved;
    };
  }, [ws, tagMap, pending]);
  return { ws, resolve };
}
